import * as React from "react";

import "./recaptcha.scss";

import {consumeContext} from "@Common/react/context";
import {
    ComponentLink,
    Link,
    makeChain,
    valueHelper as value,
}                       from "@Common/react/cst2";
import {Load2}          from "@Common/react/load2";
import {Static2}        from "@Common/react/static-2";
import {UseState}       from "@Common/react/use-state";
import {CheckboxLine}   from "@Common/ui-components/heavy/mailing-ui/create-mailing-list-modal/types/demographics/step-2-customize/form/checkbox-line/checkbox-line";
import {Responsive}     from "@Common/ui-components/responsive";
import {cache0}         from "@Common/utils/cache0";

const SKIP_INVISIBLE_KEY    = "XDQA_SKIP_INVISIBLE_RECAPTCHA";
const V2_CHECKBOX_SITEKEY   = "6LdJvSQTAAAAADlWIq9iIk5UTZlBJ45XpmBKrKup";
const V2_INVISIBLE_SITEKEY  = "6LfQIT8qAAAAALUxt8I43vWW7B2A8YOo7GBJeKyZ";

declare global {
    interface Window {
        onCaptchaLoad   ?: () => void,
    }
}

export const ReCaptcha = (props:{
    onExpiration    ?: () => void,
    onVerification  ?: (token:string) => void,
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("themeScheme"))
    // load google api
    .then("load", ({}, next:ComponentLink<typeof Load2<void>>) => (
        Load2({
            next,
            fetch: loadRecaptcha
        })
    ))
    .then(({load: {loading}}, next:Link<React.ReactNode>) => loading ? false : next())

    // "latest props"
    .then("propsStatic", ({}, next:ComponentLink<typeof Static2<Parameters<typeof ReCaptcha>[0]>>) => Static2({next}))
    .then(value(({propsStatic: {set: setProps}}) => setProps(props)))

    // avoid rerendering captcha in same element; google throws
    .then("divStatic", ({}, next:ComponentLink<typeof Static2<HTMLDivElement>>) => Static2({next}))
    .then("responsive", (_, next:ComponentLink<typeof Responsive>) => (
        <Responsive {...{
            sizes: {
                md: 446,
            },
            next,
        }}/>
    ))
    .then("checked", ({}, next:ComponentLink<typeof UseState<boolean>>) => (
        UseState({next, initValue: false})
    ))
    .then(({
        divStatic   : {get: getDiv, set: setDiv},
        propsStatic : {get: getProps},
        responsive,
        themeScheme,
        checked,
    }) => (
        <>
        {themeScheme?.internalUser && (
            CheckboxLine({
                state: {value: checked.value, onChange: (value:boolean) => {
                    checked.onChange(value);
                    getProps()?.onVerification(value ? "skip" : null);
                }},
                text: "Skip ReCAPTCHA (Internal users only)",
                xdQaName: "Skip ReCAPTCHA",
            })
        )}
        <div ref={(div) => {
            if (!div || div === getDiv()) {
                return;
            }

            grecaptcha.render(div, {
                size                : responsive.lt("md") ? "compact" : "normal",
                callback            : (token) => getProps()?.onVerification?.(token),
                'expired-callback'  : () => getProps()?.onExpiration?.(),
                sitekey             : V2_CHECKBOX_SITEKEY,
            })
            setDiv(div);
        }}/>
        </>
    ))
    .run()
);

const isInvisibleRecaptchaSkipped = ({isInternalUser}:{isInternalUser: boolean}) => (
    isInternalUser && ![undefined, null, "", "0"].includes(localStorage[SKIP_INVISIBLE_KEY])
);

export const ReCaptchaInvisible = ({next: rootNext, isInternalUser, ...props}:{
    next             : Link<JSX.Element, {
        execute: () => Promise<{token: string, expires: Promise<void>}>,
    }>,
    isInternalUser  ?: boolean,
    onExpiration    ?: () => void,
    onVerification  ?: (token: string) => void,
}) => (
    makeChain<JSX.Element>()
    .then(({}, next:Link<JSX.Element>) => (
        isInvisibleRecaptchaSkipped({ isInternalUser })
        ? rootNext({execute: async () => ({token: "skip", expires: new Promise(() => {})})})
        : next()
    ))
    .then("recaptchaLoad", ({}, next:ComponentLink<typeof Load2<void>>) => (
        Load2({next, fetch: loadRecaptcha})
    ))
    .then(({recaptchaLoad: {loading}}, next:Link<React.ReactNode>) => (
        loading
        ? rootNext({execute: () => Promise.reject("not ready")})
        : next()
    ))

    // "latest props"
    .then("propsStatic", ({}, next:ComponentLink<typeof Static2<Parameters<typeof ReCaptcha>[0]>>) => Static2({next}))
    .then(value(({propsStatic: {set: setProps}}) => setProps(props)))

    .then("resolversStatic", (
        {},
        next:ComponentLink<typeof Static2<{
            execute: (result:{token:string, expires:Promise<void>}) => void,
            expire: () => void,
        }>>
    ) => (
        Static2({next, getInitValue: () => ({})})
    ))

    // avoid rerendering captcha in same element; google throws
    .then("widgetStatic", ({}, next:ComponentLink<typeof Static2<{div: HTMLDivElement, id: number}>>) => (
        Static2({next})
    ))
    .then("widgetId", ({
        propsStatic: {get: getProps},
        resolversStatic: {get: getResolvers, set: setResolvers},
        widgetStatic: {get: getWidget, set: setWidget},
    }) => (<>
        <div ref={(div) =>
        {
            if (!div || div === getWidget()?.div) {
                return;
            }

            const id = grecaptcha.render(div, {
                size                : "invisible",
                callback            : (token) =>
                {
                    const resolvers = getResolvers();
                    resolvers.execute?.({
                        token,
                        expires: new Promise(resolve => setResolvers({...resolvers, expire: resolve})),
                    });
                    getProps()?.onVerification?.(token);
                },
                'expired-callback'  : () =>
                {
                    getResolvers().expire?.();
                    getProps()?.onExpiration?.();
                },
                sitekey             : V2_INVISIBLE_SITEKEY,
            });
            setWidget({div, id});
        }}></div>
        {rootNext({
            execute: () => new Promise(resolve =>
            {
                setResolvers({...getResolvers(), execute: resolve});
                grecaptcha.execute(getWidget()?.id);
            })
        })}
    </>))
    .run()
);

// cache to load only once ever
const loadRecaptcha = cache0(() => new Promise<void>(resolve => {
    const script    = document.createElement("script");
    script.src      = "https://www.google.com/recaptcha/api.js?onload=onCaptchaLoad";

    window.onCaptchaLoad = () => grecaptcha.ready(resolve);

    document.head.appendChild(script);
}));

